import cloneDeep from 'lodash/cloneDeep';

import {
  CLEAR_COLUMNS,
  GET_COLUMNS,
  HIDE_COLUMN,
  MOVE_COLUMN,
  SET_COLUMNS,
  SET_COLUMN_WIDTH,
  SHOW_COLUMN,
} from '../../columns/columns.store';
import { GET_GROUPING, SET_GROUPING } from '../../columns/grouping.store';
import { SET_SORTING } from '../../columns/sorting.store';
import {
  ADD_COLLAPSED_GROUPS,
  CLEAR_TABLE,
  GET_IS_ALL_GROUPS_COLLAPSED,
  GET_TABLE,
  SET_TABLE,
} from '../../table.ui.store';

import { createElement } from 'src/app/core/helpers/dom';
import { checkTableColumnsHover } from '../../../site-options/site-options';
import { getDefaultWidth } from '../../columns/columns';
import { groupTable } from '../../grouping/group';

import { TAnyFunction } from '@core/helpers';
import { WindowService } from '@core/services';
import { TTableUser } from '@project/view-models';
import { Observable, of, Subject } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { logout$ } from 'src/app/project/modules/auth/logout.service';
import { CombineCustomFieldsService } from 'src/app/project/modules/custom-fields/combine-custom-fields/combine-custom-fields.service';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import { logErrorInSentry } from 'src/app/project/modules/errors/response-error';
import { PointSearchService } from 'src/app/project/modules/points/point-search/point-search.service';
import { TPoint } from 'src/app/project/modules/points/points.model';
import * as selectedGroups from 'src/app/project/modules/points/selected-groups';
import { clearSelectedPoints } from 'src/app/project/modules/points/selected-points';
import { GET_USER } from 'src/app/project/modules/user/user.store';
import { ScrollHandlingService } from 'src/app/project/services/infinite-scrolling/scroll-handling.service';
import { ERowType } from 'src/app/project/shared/enums/row-type.enum';
import { PointAttachmentsService } from '../../../../points/point-modal/point-attachments/point-attachments.service';
import { SiteOptionsService } from '../../../site-options/site-options.service';
import { TColumn } from '../../columns/column.model';
import { TGroupedPoints } from '../../grouping/group-model';
import { CustomTableService } from '../custom-table.service';
import TableBody from '../table-body/table-body';
import TableFooter from '../table-footer/table-footer';
import { setMovingColumnsEnabled } from '../table-head/move-columns';
import TableHead from '../table-head/table-head';
import { TableLoadingService } from '../table-loading.service';
import { TTableItem, TTableParams } from '../table.model';
import { findPointIndexInTable } from '../utils/find-point-index-in-table';
import { clearTableLoadTimer, startTableLoadTimer } from './table-load-monitor';
import { buildSlowTableLoadReport, TSlowTableLoadReport } from './utils/build-slow-load-report';
import { collectGroupIds } from './utils/collect-group-ids';
import { digestColumnPreferences } from './utils/digest-column-preferences';
import { generateGroupedPointItems } from './utils/generate-grouped-points';
import { generateSelectedGroupedPointItems } from './utils/generate-selected-grouped-points';
import { getExtraTableWidth } from './utils/get-extra-table-width';
import { getGroupSize } from './utils/get-group-size';
import { getSelectElementWidth } from './utils/select-element-width';

export const FETCH_ALL_PAGE_SIZE = 100000;

export default class Table {
  workspaceId: string;
  selectPointCallback: TAnyFunction;
  selectAllPointsCallback: TAnyFunction;
  openPointCallback: TAnyFunction;
  savePreferencesCallback: TAnyFunction;
  highlightPinCallback: TAnyFunction | undefined;
  dehighlightPinCallback: TAnyFunction | undefined;

  visibleColumnIndexes: number[] = [];
  visibleColumnPositions: number[] = [];
  users: TTableUser[] = [];
  points: TPoint[] = [];
  items: TTableItem[] = [];
  width = 0;
  selectedPoints: Set<string> = new Set();
  selectedPointsCount = 0;
  hasSelectedAllPoints = false;
  isPointsSelectAllIndeterminate: boolean;
  deselectedPoints: Set<string> = new Set();
  selectedGroupCounts: Map<string, number> = new Map();
  keyword = '';
  groupedPoints: TGroupedPoints[];
  activePointId: string;
  siteOptionsService: SiteOptionsService;

  tableHead: TableHead = null;
  tableBody: TableBody = null;
  tableFooter: TableFooter = null;

  element: HTMLElement = null;

  private readonly destroy$ = new Subject<void>();

  private getCustomFieldsCallback: () => Observable<TCustomField[]>;
  private pointAttachmentsService: PointAttachmentsService;
  private visibleColumnsSet: Set<string> = new Set();
  private hiddenColumnsSet: Set<string> = new Set();
  overview: boolean;
  visibleColumns: TColumn[] = [];
  private dropdownService: DropdownService;
  defaultColumnsLoaded: boolean;
  customTableService: CustomTableService;
  pointSearchService: PointSearchService;
  scrollHandlingService: ScrollHandlingService;
  combineCustomFieldsService: CombineCustomFieldsService;
  private pageSize: number;
  private tableLoadingService: TableLoadingService;
  private windowService: WindowService;

  constructor(
    overview: boolean,
    {
      workspaceId: workspaceId,
      selectPointCallback,
      selectAllPointsCallback,
      openPointCallback,
      savePreferencesCallback,
      highlightPinCallback,
      dehighlightPinCallback,
      getCustomFieldsCallback,
      dropdownService,
      pointAttachmentsService,
      siteOptionsService,
      activePointId,
      customTableService,
      pointSearchService,
      scrollHandlingService,
      tableLoadingService,
      combineCustomFieldsService,
      windowService,
    }: TTableParams,
  ) {
    this.overview = overview;
    this.workspaceId = workspaceId;
    this.selectPointCallback = selectPointCallback;
    this.selectAllPointsCallback = selectAllPointsCallback;
    this.openPointCallback = openPointCallback;
    this.savePreferencesCallback = savePreferencesCallback;
    this.highlightPinCallback = highlightPinCallback;
    this.dehighlightPinCallback = dehighlightPinCallback;
    this.getCustomFieldsCallback = getCustomFieldsCallback;
    this.pointAttachmentsService = pointAttachmentsService;
    this.activePointId = activePointId;
    this.dropdownService = dropdownService;
    this.siteOptionsService = siteOptionsService;
    this.customTableService = customTableService;
    this.pointSearchService = pointSearchService;
    this.scrollHandlingService = scrollHandlingService;
    this.tableLoadingService = tableLoadingService;
    this.combineCustomFieldsService = combineCustomFieldsService;
    this.windowService = windowService;

    SET_TABLE(this);
    CLEAR_COLUMNS();

    this.element = this.create();

    this.tableHead = new TableHead(this);
    this.tableBody = new TableBody(
      this,
      this.pointAttachmentsService,
      this.dropdownService,
      this.windowService,
    );

    this.tableFooter = new TableFooter(this);

    this.setPageSize(!this.overview ? FETCH_ALL_PAGE_SIZE : null);
  }

  private create(): HTMLElement {
    return createElement('div', {
      attrs: {
        style: {
          height: '100%',
        },
      },
    });
  }

  setSelectedPointsCount(count: number): void {
    this.selectedPointsCount = count;
  }

  get hasSelectedPointsWithRelationships(): boolean {
    return this.points.some(
      (point) => this.selectedPoints.has(point._id) && point.hasRelationships,
    );
  }

  addSelectedGroup(groupId: string, count: number): void {
    selectedGroups.addSelectedGroup(this, groupId, count);
  }

  removeSelectedGroup(groupId: string): void {
    selectedGroups.removeSelectedGroup(this, groupId);
  }

  clearSelectedGroups(): void {
    selectedGroups.clearSelectedGroups(this);
  }

  isPointInSelectedGroup(pointId: string): boolean {
    return selectedGroups.isPointInSelectedGroup(this, pointId);
  }

  refreshSelectedGroupSelections(): void {
    selectedGroups.refreshSelectedGroupSelections(this);
  }

  recomputeSelectedPointsCount(): void {
    selectedGroups.recomputeSelectedPointsCount(this);
  }

  startLoadMonitor(): void {
    startTableLoadTimer({
      apiLoading$: this.pointSearchService.apiLoadingStatus$,
      tableLoading$: this.tableLoadingService.isTableLoading$,
      buildReport: () =>
        buildSlowTableLoadReport({
          url: window.location.href,
          overview: this.overview,
          workspaceId: this.workspaceId,
          user: GET_USER(),
          keyword: this.keyword,
          grouping: GET_GROUPING().map((column) => column.name),
          searchPayload: this.pointSearchService.getLastSearchQuery(),
        }),
    });
  }

  loadNextPage(
    {
      isOverview = false,
      size = null,
    }: {
      isOverview?: boolean;
      size?: number;
    } = {
      isOverview: false,
      size: null,
    },
  ): void {
    if (!this.pointSearchService.hasNextPage()) {
      return;
    }

    this.startLoadMonitor();

    this.reload({ reset: false, isOverview, customSize: size, isLoadNextPage: true });
  }

  reload({
    reset,
    isOverview,
    preserveTableState,
    customSize,
    isLoadNextPage,
  }: {
    reset: boolean;
    isOverview?: boolean;
    preserveTableState?: boolean;
    customSize?: number;
    isLoadNextPage?: boolean;
  }): void {
    if (reset && !isLoadNextPage) {
      this.startLoadMonitor();
    }

    if (reset) {
      this.updatePoints([]);
    }

    this.tableLoadingService.toggleSpinner(true, isLoadNextPage);
    setMovingColumnsEnabled(false);

    this.pointSearchService
      .searchPointsWithPagination({
        reset,
        isOverview,
        workspaceId: this.workspaceId,
        preserveTableState: preserveTableState,
        customSize,
      })
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(logout$),
        catchError((error) => this.handlePaginationError(error)),
      )
      .subscribe((points) => {
        this.updatePoints(points);

        if (this.tableBody.hasSpaceForMorePoints() && this.pointSearchService.hasNextPage()) {
          this.loadNextPage({ isOverview, size: customSize });

          return;
        }

        this.tableLoadingService.toggleSpinner(false);
        this.updateSelectedPointsCountIfSelectAll();
        this.refreshSelectedGroupSelections();
        this.recomputeSelectedPointsCount();

        if (isLoadNextPage) {
          this.tableBody.load(true);
        } else {
          this.load({ keepScrollPosition: true });
        }

        this.syncScrollLeftWithTableBody();
        setMovingColumnsEnabled(true);
      });
  }

  updateSelectedPointsCountIfSelectAll(): void {
    if (this.hasSelectedAllPoints) {
      this.cleanupDeselectedPoints();

      const workspace = this.pointSearchService?.getAPIKey();
      const totalCount = this.pointSearchService.getTotalCount(workspace);
      const deselectedCount = this.deselectedPoints.size;
      this.setSelectedPointsCount(totalCount - deselectedCount);
    }
  }

  private cleanupDeselectedPoints(): void {
    if (this.deselectedPoints.size === 0) {
      return;
    }

    const currentPointIds = new Set(this.points.map((point) => point._id));
    this.deselectedPoints.forEach((id) => {
      if (!currentPointIds.has(id)) {
        this.deselectedPoints.delete(id);
      }
    });

    if (this.deselectedPoints.size === 0) {
      this.isPointsSelectAllIndeterminate = false;
    }
  }

  load({ keepScrollPosition }: { keepScrollPosition: boolean }): void {
    const scrollPosition = keepScrollPosition
      ? this.tableBody.virtualScroller.scrollElement.scrollLeft
      : 0;

    this.tableHead.updateFrozenColumnWidth();
    this.tableBody.updateFrozenColumnWidth();
    this.tableFooter.updateFrozenColumnWidth();
    this.updateVisibleColumnIndexes();
    this.updateVisibleColumnPositions();

    // Sort, group and build items once, then render the body a single time.
    // Head and footer only rebuild their own cells; their column cells are
    // reconciled by the body render's scrollLeftCallback.
    this.digestColumnPreferences();
    this.generateItems();

    this.tableHead.loadStructure();
    this.tableFooter.loadStructure();

    this.tableBody.load(keepScrollPosition);

    this.tableHead.scrollLeft(scrollPosition);
    this.tableFooter.scrollLeft(scrollPosition);
    setMovingColumnsEnabled(true);
  }

  clear(): void {
    this.points = [];
    this.items = [];
    this.users = [];
    CLEAR_COLUMNS();
    this.updateWidth(0);
    this.tableHead.clear();
    this.tableBody.clear();
    this.tableFooter.clear();
  }

  destroy(): void {
    clearTableLoadTimer();
    this.destroy$.next();
    this.destroy$.complete();

    this.clear();

    this.tableHead?.destroy();
    this.tableBody?.destroy();
    this.tableFooter?.destroy();
    this.tableHead = null;
    this.tableBody = null;
    this.tableFooter = null;

    if (GET_TABLE() === this) {
      CLEAR_TABLE();
    }

    this.element = null;
  }

  updateWidth(width?: number): void {
    if (width === undefined && GET_COLUMNS().length) {
      const grouping = GET_GROUPING();
      const selectWidth = grouping.length ? getSelectElementWidth(grouping.length) : 40;

      this.width =
        selectWidth +
        (GET_COLUMNS() as any).reduce((columnA, columnB) => {
          let aWidth = 0;
          let bWidth = 0;

          if (typeof columnA === 'number') {
            aWidth = columnA;
          } else if (!columnA.hidden) {
            aWidth = columnA.width;
          }

          if (!columnB.hidden) {
            bWidth = columnB.width;
          }

          return aWidth + bWidth;
        });
    } else {
      this.width = width;
    }
  }

  collapseGroup(groupId: string): void {
    const groupSize = getGroupSize(groupId);

    this.updateGrouping(groupSize, {
      shouldLoadMore: !this.collapsedGroupCoversSearchTotal(groupSize),
    });
  }

  private collapsedGroupCoversSearchTotal(groupSize: number | undefined): boolean {
    if (groupSize === undefined) {
      return false;
    }

    const apiKey = this.pointSearchService.getAPIKey();

    return groupSize === this.pointSearchService.getTotalCount(apiKey);
  }

  updateGrouping(
    customSize: number = 0,
    { shouldLoadMore = true }: { shouldLoadMore?: boolean } = {},
  ): void {
    if (!this.groupedPoints?.length) {
      this.sortTable();

      return;
    }

    const headScroll = this.tableHead.element.scrollLeft;

    this.items = generateGroupedPointItems(this.groupedPoints);
    this.tableBody.virtualScroller.load(this.items, true);

    this.tableHead.scrollLeft(headScroll);

    if (shouldLoadMore) {
      this.tableBody.checkIfNeedsToLoadMore(customSize);
    }
  }

  sortTable({
    updatePreferences = false,
    keepScrollPosition = true,
  }: {
    updatePreferences?: boolean;
    keepScrollPosition?: boolean;
  } = {}): void {
    const headScroll = this.tableHead.element.scrollLeft;

    this.digestColumnPreferences();
    this.generateItems();
    this.tableBody.virtualScroller.load(this.items, keepScrollPosition);

    if (updatePreferences) {
      this.savePreferencesCallback();
    }

    this.tableHead.scrollLeft(headScroll);
  }

  updateColumns({
    columns,
    updatePreferences = true,
    reset = false,
  }: { columns?: TColumn[]; updatePreferences?: boolean; reset?: boolean } = {}): void {
    this.visibleColumns.length = 0;

    if (columns === undefined || reset) {
      this.generateColumns();
    } else {
      this.processExistingColumns(columns, updatePreferences);
    }

    this.digestColumnPreferences();
    this.tableBody.updateColumns();
  }

  toggleColumn(column: TColumn): void {
    const columnIndex = GET_COLUMNS().findIndex(
      (searchedColumn) =>
        searchedColumn.name === column.name && searchedColumn.index === column.index,
    );

    if (!column.hidden) {
      HIDE_COLUMN(columnIndex);
    } else {
      SHOW_COLUMN(columnIndex);
    }
  }

  moveColumn(columnIndex: number, destinationIndex: number): void {
    const column = GET_COLUMNS()[columnIndex];

    if (!column.hidden) {
      this.tableHead.moveColumn();
      this.tableBody.moveColumn(columnIndex, destinationIndex);
      this.tableFooter.moveColumn();
    }

    MOVE_COLUMN(columnIndex, destinationIndex);
  }

  updateVisibleColumnIndexes(): void {
    this.visibleColumnIndexes.length = 0;
    this.visibleColumnIndexes = GET_COLUMNS()
      .filter((column) => !column.hidden)
      .map((column) => column.index);

    checkTableColumnsHover();
  }

  updateVisibleColumnPositions(): void {
    const columns = GET_COLUMNS();
    this.visibleColumnPositions.length = 0;

    this.visibleColumnIndexes.forEach((columnIndex, index) => {
      if (index === 0) {
        if (this.visibleColumnIndexes.length > 1) {
          this.visibleColumnPositions[index + 1] = columns[columnIndex].width;
        }

        this.visibleColumnPositions[index] = 0;
      } else if (this.visibleColumnIndexes.length > index + 1) {
        if (this.visibleColumnIndexes.length > index + 1 && columns[columnIndex]) {
          this.visibleColumnPositions[index + 1] =
            this.visibleColumnPositions[index] + columns[columnIndex].width;
        }
      }
    });
  }

  updatePoints(points: TPoint[]): void {
    this.points = cloneDeep(points);

    this.generateItems();
  }

  highlightPoint(sequenceNumber: number): void {
    this.tableBody.highlightPoint(sequenceNumber);
  }

  dehighlightPoint(sequenceNumber: number): void {
    this.tableBody.dehighlightPoint(sequenceNumber);
  }

  deselectPoints(): void {
    clearSelectedPoints();
    this.tableBody.deselectPoints();
    this.tableHead.deselectPoints();

    this.sortTable();
  }

  addPoint(point: TPoint): void {
    this.points.push(point);
    this.sortTable();
  }

  getVisiblePointIds(): string[] {
    return this.points.map((point) => point._id);
  }

  activatePoint(pointSequenceNumber: string | number): void {
    const pointSwitchingBlocked = this.pointAttachmentsService.blocksPointSwitching();

    if (!pointSwitchingBlocked) {
      this.deactivatePoint();

      const pointIndexInTable = findPointIndexInTable(this.points, this.items, pointSequenceNumber);
      const pointElement = this.tableBody.virtualScroller.dataElements[pointIndexInTable];

      if (pointElement) {
        pointElement.classList.add('table__row--active');
      }
    }
  }

  // Cells

  loadAvatars(users: TTableUser[]): void {
    this.users = users;

    this.tableBody.load(true);
  }

  setUsers(users: TTableUser[]): void {
    this.users = users;
  }

  setKeyword(keyword: string): void {
    this.keyword = keyword;
  }

  setWorkspaceId(workspaceId: string): void {
    this.workspaceId = workspaceId;
  }

  setActivePointId(pointId: string): void {
    this.activePointId = pointId;

    this.activatePointFromId(this.activePointId);
  }

  generateColumns(): void {
    this.defaultColumnsLoaded = true;

    this.getCustomFieldsCallback()
      .pipe(
        takeUntil(this.destroy$),
        tap((customFields) => {
          this.customTableService.checkColumns(customFields, this.workspaceId);
          this.load({ keepScrollPosition: true });
        }),
      )
      .subscribe();
  }

  checkColumnVisibility(): { columnsToHide: TColumn[]; columnsToShow: TColumn[] } {
    const columnsToHide = [];
    const columnsToShow = [];

    GET_COLUMNS().forEach((column) => {
      if (column.hidden && this.visibleColumnsSet.has(column.name)) {
        this.visibleColumnsSet.delete(column.name);
        this.hiddenColumnsSet.add(column.name);
        columnsToHide.push(column);
      } else if (!column.hidden && this.hiddenColumnsSet.has(column.name)) {
        this.hiddenColumnsSet.delete(column.name);
        this.visibleColumnsSet.add(column.name);
        columnsToShow.push(column);
      }
    });

    return {
      columnsToHide,
      columnsToShow,
    };
  }

  private digestColumnPreferences(): void {
    const { groupingColumns, sortedColumns } = digestColumnPreferences();

    SET_GROUPING(groupingColumns.sort((a, b) => a.groupIndex - b.groupIndex));
    SET_SORTING(sortedColumns);

    // get items

    this.generateItems();

    this.tableHead.updateFrozenColumnWidth();
    this.tableBody.updateFrozenColumnWidth();
    this.tableFooter.updateFrozenColumnWidth();
  }

  private activatePointFromId(pointId: string): void {
    const point = this.points.find((searchedPoint) => searchedPoint._id === pointId);

    this.deactivatePoint();

    if (point) {
      this.activatePoint(point.sequenceNumber);
    }
  }

  private deactivatePoint(): void {
    this.tableBody.virtualScroller.dataElements.forEach((element) => {
      if (element?.classList) {
        element.classList.remove('table__row--active');
      }
    });
  }

  private generateItems(): void {
    const grouping = GET_GROUPING();

    this.items.length = 0;

    if (grouping.length) {
      this.generateGroupedPoints();
    } else {
      this.generateUngroupedPoints();
    }

    this.updateWidth();
  }

  private processExistingColumns(columns: TColumn[], updatePreferences: boolean = true): void {
    columns.forEach((column, index) => {
      if (!column.hasOwnProperty('width')) {
        SET_COLUMN_WIDTH(index, getDefaultWidth(column.name));
      }

      if (column.hidden) {
        this.hiddenColumnsSet.add(column.name);
      } else {
        this.visibleColumnsSet.add(column.name);
        this.visibleColumns.push(column);
      }
    });

    this.setColumns(columns);
    this.defaultColumnsLoaded = false;

    this.getCustomFieldsCallback()
      .pipe(
        takeUntil(this.destroy$),
        tap((customFields) => {
          this.setColumns(columns);
          this.defaultColumnsLoaded = false;
          this.customTableService.checkColumns(customFields, this.workspaceId, updatePreferences);
          this.load({ keepScrollPosition: true });
        }),
      )
      .subscribe();
  }

  private setColumns(columns: TColumn[]): void {
    SET_COLUMNS(columns);
  }

  private generateUngroupedPoints(): void {
    this.tableBody.virtualScroller.updateExtraWidth(0);
    this.tableBody.virtualScroller.updateFrozenColumnWidth(getExtraTableWidth());
    this.element.classList.remove('table__body--grouped');

    this.items = this.points
      .filter((point) => point?._id)
      .map((point, index) => ({
        type: ERowType.POINT,
        index: index,
        pointId: point._id,
      }));
  }

  private generateGroupedPoints(): void {
    this.groupedPoints = groupTable({ _points: this.points, pagination: true });

    if (GET_IS_ALL_GROUPS_COLLAPSED()) {
      ADD_COLLAPSED_GROUPS(collectGroupIds(this.groupedPoints));
    }

    this.tableBody.virtualScroller.updateExtraWidth(getExtraTableWidth());
    this.tableBody.virtualScroller.updateFrozenColumnWidth(46);
    this.element.classList.add('table__body--grouped');

    this.items = generateGroupedPointItems(this.groupedPoints);
  }

  getSelectedItems(): TTableItem[] {
    const grouping = GET_GROUPING();
    const groupingEnabled = grouping.length > 0;

    return groupingEnabled
      ? generateSelectedGroupedPointItems(this.groupedPoints, this.selectedPoints)
      : this.items.filter((item) => this.selectedPoints.has(item.pointId));
  }

  getPoints(): TPoint[] {
    return this.points;
  }

  setPageSize(customPageSize?: number): void {
    const tableRowHeight = 36;

    this.pageSize =
      customPageSize > 0 ? customPageSize : Math.ceil((window.innerHeight / tableRowHeight) * 2);

    this.pointSearchService.setPageRows(this.pageSize);
  }

  private syncScrollLeftWithTableBody(): void {
    const scrollPosition = this.tableBody.virtualScroller.scrollElement.scrollLeft;
    this.tableHead.scrollLeft(scrollPosition);
    this.tableFooter.scrollLeft(scrollPosition);
  }

  private handlePaginationError(error: unknown): Observable<null> {
    clearTableLoadTimer();
    this.tableLoadingService.toggleSpinner(false);
    this.tableLoadingService.showLoadingPointsError();
    this.load({ keepScrollPosition: true });
    logErrorInSentry({
      message: `Error triggering searchPointsWithPagination in Table.reload, ${error}`,
      error,
    });
    return of(null);
  }
}
